import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { can } from '../../utils/permissions';
import { saveText } from '../../utils/download';
import { useHtmlDir } from '../organic/motion';
import { CandidateSafety, SafetyBanner } from '../TaxonSafety';

/*
 * The species and family bioprofile (routes/taxon_bioprofile.py, migration
 * 0075): literature, bioactive compounds, degradative enzymes and biotech
 * applications, every claim citing a paper (PMID / DOI).
 *
 * WHICH BUCKETS A PLACE SHOWS
 *   full     every place: the Species Catalog, an isolate's profile, every
 *            identification, an AI candidate
 *   ai_safe  literature and compounds only: what GET ?tier=ai_safe answers,
 *            and what AI answers saved before 2026-10-21 carry
 * The server already answers each place with its tier; `tier` here is the
 * second lock, so a component handed more than its place may show still
 * shows only what it may.
 *
 * AN UNCONFIRMED IDENTIFICATION (an AI candidate, or one no expert has
 * approved: `confirmed: false`) is shown whole under a permanent label: these
 * are literature claims about the taxon, not tests of the specimen (the
 * user's decision of 2026-10-20, docs/FIELD_AI_VISION_SPEC.md section 16).
 *
 * Toxicity comes first, then the note that this is research data. A bucket a
 * species has nothing in shows its family's claims under a badge saying so.
 * Claims the pipeline found and no expert has reviewed say so too.
 */
export const BUCKETS = ['literature', 'secondary_metabolites', 'degradative_enzymes', 'biotech_applications'];
export const AI_SAFE_BUCKETS = ['literature', 'secondary_metabolites'];
const ICON = { literature: '📚', secondary_metabolites: '🧪', degradative_enzymes: '🧬', biotech_applications: '🏭' };
const LAB_FULL_ACCESS = ['collection_manager', 'internal_expert', 'lab_technician', 'lab_manager', 'read_only'];

export const allowedBuckets = tier => (tier === 'full' ? BUCKETS : AI_SAFE_BUCKETS);

/** The buckets of `profile` this tier may show that hold anything, in the fixed order. */
export function shownBuckets(profile, tier) {
  const buckets = profile?.buckets || {};
  return allowedBuckets(tier).filter(b => (buckets[b]?.items || []).length > 0);
}

function classLabel(profile, bucket, item, lang) {
  const label = item.class_label || profile?.classes?.[`${bucket}/${item.class_key}`];
  if (!label) return item.class_key.replace(/_/g, ' ');
  return (lang.startsWith('he') ? label.he : label.en) || label.en || item.class_key;
}

function Citation({ id, source, t }) {
  if (!source) return null;
  const links = [];
  if (source.pmid) {
    links.push(
      <a key="pmid" href={`https://europepmc.org/article/MED/${source.pmid}`} target="_blank" rel="noopener noreferrer"
         title={t('tip.pmid')} dir="ltr">PMID {source.pmid}</a>);
  }
  if (source.doi) {
    links.push(
      <a key="doi" href={`https://doi.org/${source.doi}`} target="_blank" rel="noopener noreferrer"
         title={t('tip.doi')} dir="ltr">DOI {source.doi}</a>);
  }
  return (
    <li style={styles.citation} data-testid="bioprofile-citation" data-source={id}>
      <span dir="ltr">{source.citation}</span>
      {links.length > 0 && <span style={styles.links}>{links.reduce((all, l, i) => (i ? [...all, ' · ', l] : [l]), [])}</span>}
      {source.retraction_status && !['none', 'unchecked'].includes(source.retraction_status) && (
        <strong style={styles.flag}> [{t(`retraction.${source.retraction_status}`)}]</strong>
      )}
    </li>
  );
}

function Review({ claim, onReview, t }) {
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async (state, why) => {
    setBusy(true);
    try {
      await onReview(claim.fact_id, state, why);
      setRejecting(false);
      setNote('');
    } finally {
      setBusy(false);
    }
  };
  if (rejecting) {
    return (
      <span style={styles.review}>
        <label style={styles.srLabel} htmlFor={`bp-note-${claim.fact_id}`}>{t('review.note')}</label>
        <input id={`bp-note-${claim.fact_id}`} value={note} onChange={e => setNote(e.target.value)}
               placeholder={t('review.notePlaceholder')} style={styles.noteInput} data-testid="bioprofile-reject-note" />
        <button type="button" style={styles.smallBtn} disabled={busy || note.trim().length < 3}
                onClick={() => send('rejected', note)} data-testid="bioprofile-reject-confirm">{t('review.confirmReject')}</button>
        <button type="button" style={styles.smallBtn} onClick={() => setRejecting(false)}>{t('review.cancel')}</button>
      </span>
    );
  }
  return (
    <span style={styles.review}>
      {claim.review_state !== 'approved' && (
        <button type="button" style={styles.smallBtn} disabled={busy} title={t('tip.approve')}
                onClick={() => send('approved')} data-testid="bioprofile-approve">{t('review.approve')}</button>
      )}
      <button type="button" style={styles.smallBtn} disabled={busy} title={t('tip.reject')}
              onClick={() => setRejecting(true)} data-testid="bioprofile-reject">{t('review.reject')}</button>
      {claim.review_state !== 'unreviewed' && (
        <button type="button" style={styles.smallBtn} disabled={busy} title={t('tip.reopen')}
                onClick={() => send('unreviewed')} data-testid="bioprofile-reopen">{t('review.reopen')}</button>
      )}
    </span>
  );
}

function Item({ item, bucket, profile, sources, reviewer, onReview, t, lang }) {
  const claims = item.claims || [];
  const snippets = claims.map(c => c.evidence?.snippet).filter(Boolean);
  const qid = item.details?.wikidata_qid;
  return (
    <li style={styles.item} data-testid="bioprofile-item" data-review={item.review_state}>
      <div style={styles.itemHead}>
        <span style={styles.label} dir="auto">{item.label}</span>
        {bucket !== 'literature' && item.label !== classLabel(profile, bucket, item, lang) && (
          <span style={styles.classChip}>{classLabel(profile, bucket, item, lang)}</span>
        )}
        {bucket === 'literature' && <span style={styles.classChip}>{classLabel(profile, bucket, item, lang)}</span>}
        <span style={item.review_state === 'approved' ? styles.approved : styles.unreviewed}
              title={t(item.review_state === 'approved' ? 'tip.approved' : 'tip.unreviewed')}>
          {item.review_state === 'approved' ? `✓ ${t('approved')}` : t('unreviewed')}
        </span>
      </div>
      {snippets.length > 0 && (
        <details style={styles.evidence}>
          <summary style={styles.summary} title={t('tip.evidence')}>{t('evidence')}</summary>
          {snippets.slice(0, 3).map((s, i) => <blockquote key={i} style={styles.quote} dir="ltr">{s}</blockquote>)}
        </details>
      )}
      {qid && (
        <span style={styles.lotus}>
          {t('lotus')}{' '}
          <a href={`https://www.wikidata.org/wiki/${qid}`} target="_blank" rel="noopener noreferrer" dir="ltr"
             title={t('tip.wikidata')}>{qid}</a>
        </span>
      )}
      <ol style={styles.citations}>
        {(item.source_ids || []).map(id => <Citation key={id} id={id} source={sources?.[id]} t={t} />)}
      </ol>
      {reviewer && claims.length > 0 && (
        <ul style={styles.claims}>
          {claims.map(c => (
            <li key={c.fact_id} style={styles.claim}>
              <span style={styles.claimSource} dir="ltr">
                {sources?.[c.source_id]?.pmid ? `PMID ${sources[c.source_id].pmid}` : (sources?.[c.source_id]?.doi || '')}
              </span>
              <Review claim={c} onReview={onReview} t={t} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** The label an unconfirmed identification's profile always carries. */
export function UnconfirmedLabel({ t }) {
  return (
    <p role="note" style={styles.unconfirmed} data-testid="bioprofile-unconfirmed">
      <span aria-hidden="true">⚠ </span>{t('unconfirmed')}
    </p>
  );
}

/**
 * One profile as given: tabs for the buckets its tier shows that hold
 * anything. `confirmed` false (or the profile's own `confirmed: false`) adds
 * the unconfirmed label.
 */
export function BioProfileView({ profile, sources, tier = 'ai_safe', reviewer = false, onReview, compact = false,
  testId = 'bioprofile', confirmed }) {
  const { t, i18n } = useTranslation('bioprofile');
  const lang = i18n?.language || 'en';
  const rtl = useHtmlDir() === 'rtl';
  const buckets = shownBuckets(profile, tier);
  const unconfirmed = confirmed === false || (confirmed === undefined && profile?.confirmed === false);
  const [active, setActive] = useState(null);
  const refs = useRef({});
  const current = buckets.includes(active) ? active : buckets[0];
  const base = useRef(`bp-${Math.random().toString(36).slice(2, 9)}`).current;

  if (!buckets.length) {
    return compact ? null : <p style={styles.none} data-testid={`${testId}-empty`}>{t('empty')}</p>;
  }
  const move = (e) => {
    const i = buckets.indexOf(current);
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    let next = null;
    if (e.key === forward) next = buckets[(i + 1) % buckets.length];
    else if (e.key === back) next = buckets[(i - 1 + buckets.length) % buckets.length];
    else if (e.key === 'Home') next = buckets[0];
    else if (e.key === 'End') next = buckets[buckets.length - 1];
    if (!next) return;
    e.preventDefault();
    setActive(next);
    refs.current[next]?.focus();
  };
  const entry = profile.buckets[current];
  const familyLevel = entry.level === 'family';

  return (
    <div data-testid={testId} data-tier={tier} data-confirmed={unconfirmed ? 'false' : 'true'}
         style={compact ? styles.compact : styles.view}>
      {unconfirmed && <UnconfirmedLabel t={t} />}
      <div role="tablist" aria-label={t('tabs')} style={styles.tabs} onKeyDown={move}>
        {buckets.map(b => (
          <button key={b} type="button" role="tab" id={`${base}-tab-${b}`} aria-selected={current === b}
                  aria-controls={`${base}-panel-${b}`} tabIndex={current === b ? 0 : -1}
                  ref={(el) => { refs.current[b] = el; }} onClick={() => setActive(b)}
                  style={current === b ? styles.tabOn : styles.tab} data-testid={`bioprofile-tab-${b}`}
                  title={t(`tip.bucket.${b}`)}>
            <span aria-hidden="true">{ICON[b]} </span>{t(`buckets.${b}`)}
            <span style={styles.count}> ({profile.buckets[b].total ?? profile.buckets[b].items.length})</span>
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${base}-panel-${current}`} aria-labelledby={`${base}-tab-${current}`} tabIndex={0}
           style={styles.panel}>
        {familyLevel && (
          <p style={styles.familyBadge} data-testid="bioprofile-family-level">
            {t('familyLevel', { family: entry.taxon })}
          </p>
        )}
        <ul style={styles.items}>
          {entry.items.map(item => (
            <Item key={`${item.class_key}|${item.label}`} item={item} bucket={current} profile={profile}
                  sources={sources} reviewer={reviewer} onReview={onReview} t={t} lang={lang} />
          ))}
        </ul>
        {(entry.total || 0) > entry.items.length && (
          <p style={styles.none}>{t('more', { shown: entry.items.length, total: entry.total })}</p>
        )}
      </div>
    </div>
  );
}

/** The research note every profile carries, below the toxicity warnings. */
export function ResearchNote({ tier, t }) {
  return (
    <p role="note" style={styles.note} data-testid="bioprofile-note">
      {t('researchNote')}
      {tier === 'ai_safe' && <> {t('aiSafeNote')}</>}
    </p>
  );
}

function download(name, family, format, t, setError) {
  return Promise.resolve(api.post('/export/bioprofile', { format, names: [name, family].filter(Boolean) },
                                  { responseType: 'text', transformResponse: [d => d] }))
    .then((res) => {
      const body = typeof res?.data === 'string' ? res.data : JSON.stringify(res?.data ?? {});
      const file = `bioprofile-${name.replace(/\s+/g, '_')}.${format}`;
      saveText(body, file, format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json');
    })
    .catch(() => setError(t('export.failed')));
}

/**
 * Everything the bank holds about one name, fetched on its own: the Species
 * Catalog, an isolate's Taxonomy tab, a strain. `tier` is the place's;
 * `confirmed` false when the name is an unconfirmed identification's.
 */
export function SpeciesBioProfile({ name, tier = 'full', user, heading, testId = 'species-bioprofile',
  confirmed = true }) {
  const { t } = useTranslation('bioprofile');
  const { t: tAi } = useTranslation('ai');
  const [state, setState] = useState({ status: 'loading', data: null });
  const [error, setError] = useState(null);
  const reviewer = tier === 'full' && Boolean(user) && can(user, 'bioprofile:review');
  const exporter = tier === 'full' && (user?.roles || []).some(r => LAB_FULL_ACCESS.includes(r));

  const load = useCallback(() => {
    if (!name) return () => {};
    let live = true;
    setState(s => ({ status: s.data ? 'refreshing' : 'loading', data: s.data }));
    Promise.resolve(api.get('/taxonomy/bioprofile', { params: { name, tier } }))
      .then(res => { if (live) setState({ status: 'ready', data: res?.data || null }); })
      .catch(() => { if (live) setState({ status: 'failed', data: null }); });
    return () => { live = false; };
  }, [name, tier]);

  useEffect(() => load(), [load]);

  const onReview = async (factId, reviewState, note) => {
    setError(null);
    try {
      await api.put(`/taxonomy/bioprofile/facts/${factId}/review`, { state: reviewState, ...(note ? { note } : {}) });
      load();
    } catch {
      setError(t('review.failed'));
    }
  };

  if (!name) return null;
  const data = state.data;
  const profile = data?.profile;
  return (
    <section data-testid={testId} style={styles.section} aria-label={t('title')}>
      <h4 style={styles.heading}>{heading || t('title')}: <em dir="ltr">{name}</em></h4>
      {state.status === 'loading' && <p style={styles.none}>{t('loading')}</p>}
      {state.status === 'failed' && (
        <p role="alert" style={styles.none}>
          {t('failed')}{' '}
          <button type="button" style={styles.smallBtn} onClick={load}>{t('retry')}</button>
        </p>
      )}
      {data && (
        <>
          {data.safety && <SafetyBanner safety={data.safety} t={tAi} />}
          {data.taxon_safety && <CandidateSafety p={{ safety: data.taxon_safety }} t={tAi} />}
          <ResearchNote tier={tier} t={t} />
          <BioProfileView profile={profile} sources={data.sources} tier={tier} reviewer={reviewer}
                          onReview={onReview} testId={`${testId}-view`} confirmed={confirmed} />
          {error && <p role="alert" style={styles.error}>{error}</p>}
          {exporter && shownBuckets(profile, tier).length > 0 && (
            <div style={styles.exportRow}>
              {['csv', 'json'].map(format => (
                <button key={format} type="button" style={styles.smallBtn} title={t('tip.export')}
                        onClick={() => download(name, profile?.family, format, t, setError)}
                        data-testid={`bioprofile-export-${format}`}>
                  {t(`export.${format}`)}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The profile an identification was saved with (identification_history,
 * dna_identifications), all four buckets, labelled unconfirmed until the row
 * is verified; a row saved before migration 0075 has none and shows the
 * profile as it is now. Only a verified one offers review and export.
 */
export function AttachedBioProfile({ stored, verified, name, user, testId = 'attached-bioprofile' }) {
  const { t } = useTranslation('bioprofile');
  const tier = 'full';
  const confirmed = Boolean(verified);
  if (!stored) {
    return name ? <SpeciesBioProfile name={name} tier={tier} user={verified ? user : null} testId={testId}
                                     heading={t('attachedTitle')} confirmed={confirmed} /> : null;
  }
  return (
    <section data-testid={testId} style={styles.section} aria-label={t('attachedTitle')}>
      <h4 style={styles.heading}>{t('attachedTitle')}: <em dir="ltr">{stored.name || name}</em></h4>
      <ResearchNote tier={tier} t={t} />
      <BioProfileView profile={stored} sources={stored.sources} tier={tier} testId={`${testId}-view`}
                      confirmed={confirmed} />
      {stored.computed_at && <p style={styles.none}>{t('attachedAt', { at: stored.computed_at.slice(0, 10) })}</p>}
    </section>
  );
}

const styles = {
  section: {
    display: 'flex', flexDirection: 'column', gap: 6, padding: 10, borderRadius: 8, marginTop: 10,
    border: '1px solid var(--c-info-soft-5)', background: 'var(--c-info-soft-4)',
  },
  heading: { margin: 0, fontSize: 13, color: 'var(--c-info-2-fg)' },
  view: { display: 'flex', flexDirection: 'column', gap: 6 },
  compact: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 },
  tabs: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  tab: {
    padding: '4px 8px', borderRadius: 6, border: '1px solid var(--c-border)', background: 'var(--c-surface)',
    color: 'var(--c-text-body-2-fg)', fontSize: 12, cursor: 'pointer',
  },
  tabOn: {
    padding: '4px 8px', borderRadius: 6, border: '1px solid var(--c-info-2-fg)', background: 'var(--c-info-soft-5)',
    color: 'var(--c-info-2-fg)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  count: { opacity: 0.75 },
  panel: { display: 'flex', flexDirection: 'column', gap: 4 },
  familyBadge: {
    margin: 0, padding: '4px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    background: 'var(--c-warning-soft-6)', color: 'var(--c-warning-text-6-fg)', alignSelf: 'flex-start',
  },
  items: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  item: { padding: '6px 8px', borderRadius: 6, background: 'var(--c-surface)', border: '1px solid var(--c-border)' },
  itemHead: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' },
  classChip: { fontSize: 11, padding: '1px 6px', borderRadius: 10, background: 'var(--c-info-soft-5)', color: 'var(--c-info-2-fg)' },
  approved: { fontSize: 11, color: 'var(--c-success-4-fg)' },
  unreviewed: { fontSize: 11, color: 'var(--c-text-muted-2-fg)', fontStyle: 'italic' },
  evidence: { fontSize: 12, marginTop: 4 },
  summary: { cursor: 'pointer', color: 'var(--c-info-2-fg)', fontSize: 12 },
  quote: { margin: '4px 0', paddingInlineStart: 8, borderInlineStart: '3px solid var(--c-border)', fontSize: 12 },
  lotus: { display: 'block', fontSize: 11, marginTop: 2, color: 'var(--c-text-muted-2-fg)' },
  citations: { margin: '4px 0 0', paddingInlineStart: 18, display: 'flex', flexDirection: 'column', gap: 2 },
  citation: { fontSize: 11, lineHeight: 1.45, overflowWrap: 'anywhere' },
  links: { marginInlineStart: 6 },
  flag: { color: 'var(--c-danger-4-fg)' },
  claims: { listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  claim: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 11 },
  claimSource: { color: 'var(--c-text-muted-2-fg)' },
  review: { display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' },
  smallBtn: {
    padding: '2px 8px', fontSize: 11, borderRadius: 6, border: '1px solid var(--c-border)',
    background: 'var(--c-surface)', cursor: 'pointer', minHeight: 24,
  },
  noteInput: { fontSize: 12, padding: '2px 6px', minWidth: 160 },
  srLabel: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  note: { margin: 0, fontSize: 12, color: 'var(--c-warning-text-7-fg)', fontWeight: 600, lineHeight: 1.45 },
  unconfirmed: {
    margin: 0, padding: '4px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, lineHeight: 1.45,
    background: 'var(--c-warning-soft-6)', color: 'var(--c-warning-text-6-fg)', alignSelf: 'flex-start',
  },
  none: { margin: 0, fontSize: 12, color: 'var(--c-text-muted-2-fg)' },
  error: { margin: 0, fontSize: 12, color: 'var(--c-danger-4-fg)' },
  exportRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
};
